import { useId } from "react";
import { motion } from "framer-motion";
import { AnimatedSvg } from "@/components";
import { OPPORTUNITY_WINDOW } from "@/content";

const SVG_STYLE = {
  width: "100%",
  height: "auto",
  fontFamily: '"Founders Grotesk", "Helvetica Neue", Arial, sans-serif',
  fontWeight: 300,
} as const;

const WEEKS = [
  [70, "0"],
  [220, "2"],
  [370, "4"],
  [520, "6"],
  [670, "8"],
  [820, "10"],
] as const;

export const OpportunityWindow = () => {
  const hatchId = useId();

  return (
    <AnimatedSvg
      viewBox="0 0 880 280"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={OPPORTUNITY_WINDOW.ariaLabel}
      style={SVG_STYLE}
    >
      <defs>
        <pattern id={hatchId} patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="8" stroke="#cccac8" strokeWidth="1" />
        </pattern>
      </defs>

      <motion.text
        x="295"
        y="44"
        textAnchor="middle"
        fill="#be4720"
        fontSize="11"
        letterSpacing="1.4"
        variants={{
          hidden: { opacity: 0, y: 5 },
          visible: { opacity: 1, y: 0, transition: { delay: 0.58, duration: 0.3 } },
        }}
      >
        WINDOW OPEN · STILL A TREND
      </motion.text>
      <motion.text
        x="670"
        y="44"
        textAnchor="middle"
        fill="#6e6e6b"
        fontSize="11"
        letterSpacing="1.4"
        variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { delay: 1.16, duration: 0.35 } } }}
      >
        WINDOW CLOSED · TREND IS OVER
      </motion.text>

      <motion.rect
        data-scroll-graphic-window
        x="70"
        y="60"
        height="145"
        fill="#be4720"
        opacity=".09"
        variants={{ hidden: { width: 0 }, visible: { width: 450, transition: { duration: 0.7, ease: "easeOut" } } }}
      />
      <motion.rect
        x="520"
        y="60"
        width="300"
        height="145"
        fill={`url(#${hatchId})`}
        variants={{ hidden: { opacity: 0 }, visible: { opacity: 0.55, transition: { delay: 0.88, duration: 0.35 } } }}
      />

      <motion.path
        d="M520 52 V213"
        stroke="#be4720"
        strokeWidth="1.5"
        strokeDasharray="5 4"
        fill="none"
        variants={{
          hidden: { pathLength: 0, opacity: 0 },
          visible: { pathLength: 1, opacity: 1, transition: { delay: 0.67, duration: 0.4 } },
        }}
      />

      <motion.path
        d="M70 112 H145"
        stroke="#be4720"
        strokeWidth="12"
        strokeLinecap="round"
        fill="none"
        variants={{
          hidden: { pathLength: 0, opacity: 0 },
          visible: { pathLength: 1, opacity: 1, transition: { delay: 0.26, duration: 0.35 } },
        }}
      />
      <motion.circle
        cx="145"
        cy="112"
        r="7.5"
        fill="#be4720"
        stroke="#f5f3f0"
        strokeWidth="2"
        variants={{
          hidden: { opacity: 0, scale: 0 },
          visible: { opacity: 1, scale: 1, transition: { delay: 0.54, type: "spring", stiffness: 420, damping: 18 } },
        }}
      />
      <motion.text
        x="164"
        y="117"
        fill="#1a1919"
        fontSize="14"
        variants={{
          hidden: { opacity: 0, x: -8 },
          visible: { opacity: 1, x: 0, transition: { delay: 0.62, duration: 0.35 } },
        }}
      >
        You on LOOP: week 1
      </motion.text>

      <motion.path
        d="M70 166 H520"
        stroke="#6e6e6b"
        strokeWidth="12"
        strokeLinecap="round"
        fill="none"
        variants={{
          hidden: { pathLength: 0, opacity: 0 },
          visible: { pathLength: 1, opacity: 1, transition: { delay: 1.03, duration: 0.7, ease: "easeInOut" } },
        }}
      />
      <motion.path
        d="M520 166 H670"
        stroke="#b2b1af"
        strokeWidth="12"
        strokeLinecap="round"
        fill="none"
        variants={{
          hidden: { pathLength: 0, opacity: 0 },
          visible: { pathLength: 1, opacity: 1, transition: { delay: 1.7, duration: 0.35 } },
        }}
      />
      <motion.circle
        cx="670"
        cy="166"
        r="7.5"
        fill="#6e6e6b"
        stroke="#f5f3f0"
        strokeWidth="2"
        variants={{
          hidden: { opacity: 0, scale: 0 },
          visible: { opacity: 1, scale: 1, transition: { delay: 2.02, type: "spring", stiffness: 400, damping: 18 } },
        }}
      />
      <motion.text
        x="689"
        y="171"
        fill="#1a1919"
        fontSize="14"
        variants={{
          hidden: { opacity: 0, x: -8 },
          visible: { opacity: 1, x: 0, transition: { delay: 2.1, duration: 0.35 } },
        }}
      >
        Everyone else: week 8
      </motion.text>

      <motion.g
        variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { delay: 0.62, duration: 0.25 } } }}
        stroke="#cccac8"
        strokeWidth="1"
        fill="none"
      >
        <motion.path
          d="M70 205 H820"
          variants={{
            hidden: { pathLength: 0 },
            visible: { pathLength: 1, transition: { delay: 0.58, duration: 0.65 } },
          }}
        />
        <motion.path
          d="M70 205 v6 M220 205 v6 M370 205 v6 M520 205 v6 M670 205 v6 M820 205 v6"
          variants={{
            hidden: { pathLength: 0 },
            visible: { pathLength: 1, transition: { delay: 0.78, duration: 0.45 } },
          }}
        />
      </motion.g>
      <motion.g
        variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { delay: 0.9, duration: 0.3 } } }}
        fill="#6e6e6b"
        fontSize="11"
        textAnchor="middle"
      >
        {WEEKS.map(([x, label]) => (
          <text key={label} x={x} y="226">
            {label}
          </text>
        ))}
      </motion.g>
      <motion.text
        x="445"
        y="252"
        textAnchor="middle"
        fill="#6e6e6b"
        fontSize="12"
        letterSpacing="1.4"
        variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { delay: 1.04, duration: 0.3 } } }}
      >
        WEEKS
      </motion.text>
    </AnimatedSvg>
  );
};
